import { seedApplications } from '../data/ownerAdmin';
import { brtaOwners, kpiBase } from '../data/applicationReview';
import type { Tone } from '../types/domain';
import type { OwnerApplication } from '../types/owner';
import type { QueueKey, ReviewAction, ReviewRole, StageQueue } from '../types/review';

export const queueTabs: { key: QueueKey; label: string }[] = [
  { key: 'all', label: 'সকল আবেদন' },
  { key: 'new', label: 'নতুন আবেদন' },
  { key: 'mine', label: 'আমার কাছে অপেক্ষমাণ' },
  { key: 'processing', label: 'যাচাই প্রক্রিয়াধীন' },
  { key: 'awaiting', label: 'অনুমোদনের অপেক্ষায়' },
  { key: 'returned', label: 'সংশোধনের জন্য ফেরত' },
  { key: 'approved', label: 'অনুমোদিত' },
  { key: 'rejected', label: 'বাতিল' }
];

export const queueMeta: Record<StageQueue, { label: string; tone: Tone }> = {
  new: { label: 'নতুন আবেদন', tone: 'blue' },
  processing: { label: 'যাচাই প্রক্রিয়াধীন', tone: 'orange' },
  awaiting: { label: 'অনুমোদনের অপেক্ষায়', tone: 'blue' },
  returned: { label: 'সংশোধন প্রয়োজন', tone: 'orange' },
  approved: { label: 'অনুমোদিত', tone: 'green' },
  rejected: { label: 'বাতিল', tone: 'red' }
};

export function queueOf(app: OwnerApplication): StageQueue {
  if (app.status === 'বাতিল') return 'rejected';
  if (app.status === 'সংশোধন প্রয়োজন') return 'returned';
  if (app.stage >= 5) return 'approved';
  if (app.stage === 4) return 'awaiting';
  if (app.stage <= 1 && app.status === 'অপেক্ষমাণ') return 'new';
  return 'processing';
}

export function brtaBadge(app: OwnerApplication): { label: string; tone: Tone } {
  if (app.brtaResult === 'মিলেছে') return { label: 'তথ্য মিলেছে', tone: 'green' };
  if (app.brtaResult === 'অমিল') return { label: 'তথ্যের অমিল', tone: 'red' };
  return { label: 'যাচাই অপেক্ষমাণ', tone: 'orange' };
}

export function typeLabel(app: OwnerApplication): string {
  return app.vehicleType === 'cng' ? 'সিএনজি' : 'মোটরসাইকেল';
}

const permissions: Record<ReviewRole, ReviewAction[]> = {
  'ডাটা রিভিউ অপারেটর': ['review', 'return', 'assign'],
  'যাচাই কর্মকর্তা': ['review', 'validate', 'return', 'assign', 'driver'],
  'অনুমোদনকারী কর্মকর্তা': ['approve', 'reject', 'return', 'assign', 'driver'],
  'স্টিকার অপারেটর': [],
  'সুপার অ্যাডমিন': ['review', 'validate', 'approve', 'reject', 'return', 'assign', 'driver']
};

export function can(role: ReviewRole, action: ReviewAction): boolean {
  return permissions[role].includes(action);
}

export function brtaOwnerOf(app: OwnerApplication): string {
  return brtaOwners[app.vehicleReg] ?? app.ownerName;
}

/** Another active application for the same registration number by a different owner. */
export function findDuplicate(app: OwnerApplication, apps: OwnerApplication[]): OwnerApplication | undefined {
  return apps.find((x) => x.id !== app.id && x.vehicleReg === app.vehicleReg && x.ownerId !== app.ownerId && x.status !== 'বাতিল');
}

/** A duplicate blocks approval when the applicant is not the BRTA owner of record. */
export function isBlockingDuplicate(app: OwnerApplication, apps: OwnerApplication[]): boolean {
  return Boolean(findDuplicate(app, apps)) && brtaOwnerOf(app) !== app.ownerName;
}

function countBy(apps: OwnerApplication[], k: StageQueue): number {
  return apps.filter((a) => queueOf(a) === k).length;
}

export function kpiValues(apps: OwnerApplication[]) {
  const adj = (base: number, k: StageQueue) => base + countBy(apps, k) - countBy(seedApplications, k);
  const cngDelta = apps.filter((a) => a.vehicleType === 'cng').length - seedApplications.filter((a) => a.vehicleType === 'cng').length;
  const mclDelta = apps.filter((a) => a.vehicleType === 'motorcycle').length - seedApplications.filter((a) => a.vehicleType === 'motorcycle').length;
  const processing = adj(kpiBase.processing, 'processing');
  const awaiting = adj(kpiBase.awaiting, 'awaiting');
  return {
    total: kpiBase.total + apps.length - seedApplications.length,
    new: adj(kpiBase.new, 'new'),
    processing,
    awaiting,
    inReview: processing + awaiting,
    approved: adj(kpiBase.approved, 'approved'),
    returned: adj(kpiBase.returned, 'returned'),
    rejected: adj(kpiBase.rejected, 'rejected'),
    cng: kpiBase.cng + cngDelta,
    mcl: kpiBase.mcl + mclDelta
  };
}

export function inMyQueue(app: OwnerApplication, role: ReviewRole, user: string, assigned?: string): boolean {
  const q = queueOf(app);
  if (role === 'অনুমোদনকারী কর্মকর্তা') return q === 'awaiting';
  if (role === 'স্টিকার অপারেটর') return q === 'approved' && app.stage === 5;
  if (role === 'ডাটা রিভিউ অপারেটর') return q === 'new';
  return (q === 'processing' || q === 'new') && assigned === user;
}
